import {
	EngineId,
	NCGraphData,
	BoardUser,
	PerUserData,
	PdfUploadStatus,
	UserFile,
} from '../commonTypes';

/** Unlike other models, this is not a mongoose model. This is because we need to use this with ShareDB */

export class Board {
	public readonly _id: string;
	public title: string;
	public readonly template: string;
	public readonly description: string;
	public readonly input: string; // User's initial input for the board
	public readonly vision: string; // Vision generated from the user's input
	public readonly summary: string; // Summary of the vision
	public readonly openAIFileId: string; // file id for file search facility
	public readonly pdfSummary: string;
	public readonly setupInfo?: {
		firstRecommenderAnswer?: string;
		secondRecommenderAnswer?: string;
		createdAt: Date;
		engine: EngineId;
	};
	public users: Array<BoardUser>;
	public readonly project: { _id: string } | false;
	public readonly engine: EngineId;
	public readonly inspire: { data: NCGraphData };
	public readonly ideate: { data: NCGraphData };
	public readonly moodboard: { data: NCGraphData };
	public readonly userOne: PerUserData;
	public readonly userTwo: PerUserData;
	public readonly userThree: PerUserData;
	public readonly userFour: PerUserData;
	public readonly userFive?: PerUserData;
	public readonly userSix?: PerUserData;
	public readonly userSeven?: PerUserData;
	public readonly userEight?: PerUserData;
	public uploadedFiles: Array<UserFile>;
	public readonly pdfUploadStatus?: PdfUploadStatus;

	/**
	 * A board instance holds all information of a board, such as its users, pages, and contents.
	 */
	constructor(data: {
		_id: string;
		title: string;
		template?: string;
		description?: string;
		input?: string;
		vision?: string;
		summary?: string;
		project: { _id: string } | false;
		engine: EngineId;
		firstRecommenderAnswer?: string;
		secondRecommenderAnswer?: string;
		openAIFileId?: string;
		pdfSummary?: string;
		uploadedFiles?: Array<UserFile>;
		pdfUploadStatus?: PdfUploadStatus;
	}) {
		this._id = data._id;
		this.title = data.title;
		this.template = data.template || '';
		this.description = data.description || '';
		this.input = data.input || '';
		this.vision = data.vision || '';
		this.summary = data.summary || '';
		this.openAIFileId = data.openAIFileId || '';
		this.pdfSummary = data.pdfSummary || '';
		this.users = [];
		this.uploadedFiles = data.uploadedFiles && data.uploadedFiles.length ? data.uploadedFiles : [];
		this.pdfUploadStatus = data.pdfUploadStatus;
		this.userOne = {};
		this.userTwo = {};
		this.userThree = {};
		this.userFour = {};
		this.userFive = {};
		this.userSix = {};
		this.userSeven = {};
		this.userEight = {};
		this.project = data.project || false;
		this.engine = data.engine || 'Creative';
		this.setupInfo = {
			firstRecommenderAnswer: data.firstRecommenderAnswer,
			secondRecommenderAnswer: data.secondRecommenderAnswer,
			createdAt: new Date(),
			engine: this.engine, // Original engine selection, will not be changed even if the user changes the engine.
		};
		this.inspire = {
			data: {
				nodes: [],
				links: [],
			},
		};
		this.ideate = {
			data: {
				nodes: [],
				links: [],
				analyses: [],
			},
		};
		this.moodboard = {
			data: {
				nodes: [],
				links: [],
			},
		};
	}

	/**
	 * Adds a user to the list of users.
	 */
	addUser = (user: BoardUser) => {
		this.users.push(user);
	};
}

export const getProjectionName = (userIndex: number, numUsers: number) => {
	const indexFromEnd = numUsers - userIndex - 1;
	switch (indexFromEnd) {
		case 0:
			return 'boardsUserOne';
		case 1:
			return 'boardsUserTwo';
		case 2:
			return 'boardsUserThree';
		case 3:
			return 'boardsUserFour';
		case 4:
			return 'boardsUserFive';
		case 5:
			return 'boardsUserSix';
		case 6:
			return 'boardsUserSeven';
		case 7:
			return 'boardsUserEight';
		default:
			throw new Error('Max 8 users');
	}
};
